<script lang="ts">
	import { onMount } from 'svelte';
	import type { DocContent } from '$lib/docs/types';

	let { content }: { content: DocContent } = $props();

	let container: HTMLElement;

	// Process markdown content to HTML
	function processMarkdown(markdown: string): string {
		// Basic markdown to HTML conversion
		let html = markdown
			// Headers
			.replace(/^### (.*$)/gim, '<h3>$1</h3>')
			.replace(/^## (.*$)/gim, '<h2>$1</h2>')
			.replace(/^# (.*$)/gim, '<h1>$1</h1>')
			// Bold
			.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
			// Italic
			.replace(/\*(.+?)\*/g, '<em>$1</em>')
			// Links
			.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
			// Inline code
			.replace(/`([^`]+)`/g, '<code>$1</code>')
			// Line breaks
			.replace(/\n\n/g, '</p><p>')
			// Lists
			.replace(/^\* (.+)$/gim, '<li>$1</li>')
			.replace(/^\d+\. (.+)$/gim, '<li>$1</li>')
			// Horizontal rules
			.replace(/^---$/gim, '<hr>')
			// Blockquotes
			.replace(/^> (.+)$/gim, '<blockquote>$1</blockquote>');

		// Wrap lists in ul/ol tags
		html = html.replace(/(<li>.*<\/li>)/s, (match) => {
			const lines = match.split('\n');
			let result = '';
			let inList = false;
			
			for (const line of lines) {
				if (line.includes('<li>')) {
					if (!inList) {
						result += '<ul>';
						inList = true;
					}
					result += line;
				} else if (inList && !line.includes('<li>')) {
					result += '</ul>' + line;
					inList = false;
				} else {
					result += line;
				}
			}
			
			if (inList) {
				result += '</ul>';
			}
			
			return result;
		});

		// Wrap in paragraphs
		if (!html.startsWith('<')) {
			html = '<p>' + html + '</p>';
		}

		// Handle code blocks
		html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
			const language = lang || 'plaintext';
			const escapedCode = code
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.trim();
			return `<pre><code class="language-${language}">${escapedCode}</code></pre>`;
		});

		// Handle tables
		html = html.replace(/\|(.+)\|/g, (match, content) => {
			const cells = content.split('|').map(cell => cell.trim());
			const isHeader = cells.every(cell => cell.match(/^[-]+$/));
			
			if (isHeader) {
				return ''; // Skip separator rows
			}
			
			const cellTags = cells.map(cell => `<td>${cell}</td>`).join('');
			return `<tr>${cellTags}</tr>`;
		});

		// Wrap table rows in table
		html = html.replace(/(<tr>.*<\/tr>)/s, '<table>$1</table>');

		return html;
	}

	onMount(() => {
		// Add smooth scrolling for anchor links
		const links = container.querySelectorAll('a[href^="#"]');
		links.forEach(link => {
			link.addEventListener('click', (e) => {
				e.preventDefault();
				const target = document.querySelector(link.getAttribute('href'));
				if (target) {
					target.scrollIntoView({ behavior: 'smooth' });
				}
			});
		});
	});
</script>

<article class="markdown-content" bind:this={container}>
	{#if content}
		{@html processMarkdown(content.content)}
	{/if}
</article>

<style>
	.markdown-content {
		max-width: 100%;
		line-height: 1.7;
		color: var(--text-primary);
	}

	.markdown-content :global(h1) {
		font-size: 2.5rem;
		font-weight: 700;
		margin: 2rem 0 1.5rem;
		color: var(--text-primary);
		line-height: 1.2;
	}

	.markdown-content :global(h1:first-child) {
		margin-top: 0;
	}

	.markdown-content :global(h2) {
		font-size: 1.875rem;
		font-weight: 600;
		margin: 2.5rem 0 1rem;
		color: var(--text-primary);
		line-height: 1.3;
		padding-top: 1rem;
		border-top: 1px solid var(--border-light);
	}

	.markdown-content :global(h3) {
		font-size: 1.375rem;
		font-weight: 600;
		margin: 2rem 0 0.75rem;
		color: var(--text-primary);
		line-height: 1.4;
	}

	.markdown-content :global(h4) {
		font-size: 1.125rem;
		font-weight: 600;
		margin: 1.5rem 0 0.5rem;
		color: var(--text-primary);
	}

	.markdown-content :global(p) {
		margin: 1rem 0;
		color: var(--text-secondary);
	}

	.markdown-content :global(a) {
		color: var(--brand-primary);
		text-decoration: none;
		transition: color 0.2s ease;
	}

	.markdown-content :global(a:hover) {
		color: var(--brand-primary-dark);
		text-decoration: underline;
	}

	.markdown-content :global(ul),
	.markdown-content :global(ol) {
		margin: 1rem 0;
		padding-left: 2rem;
		color: var(--text-secondary);
	}

	.markdown-content :global(li) {
		margin: 0.5rem 0;
		line-height: 1.6;
	}

	.markdown-content :global(ul li) {
		list-style-type: disc;
	}

	.markdown-content :global(ol li) {
		list-style-type: decimal;
	}

	.markdown-content :global(ul ul),
	.markdown-content :global(ol ul) {
		margin: 0.5rem 0;
		padding-left: 1.5rem;
	}

	.markdown-content :global(ul ul li) {
		list-style-type: circle;
	}

	.markdown-content :global(code) {
		background: var(--bg-tertiary);
		color: var(--text-code);
		padding: 0.125rem 0.375rem;
		border-radius: 4px;
		font-family: 'Fira Code', 'Courier New', monospace;
		font-size: 0.875em;
	}

	.markdown-content :global(pre) {
		background: var(--bg-code);
		border: 1px solid var(--border-light);
		border-radius: 8px;
		padding: 1.5rem;
		margin: 1.5rem 0;
		overflow-x: auto;
	}

	.markdown-content :global(pre code) {
		background: none;
		padding: 0;
		color: var(--text-primary);
		font-size: 0.875rem;
		line-height: 1.6;
	}

	.markdown-content :global(blockquote) {
		border-left: 4px solid var(--brand-primary);
		padding-left: 1.5rem;
		margin: 1.5rem 0;
		color: var(--text-secondary);
		font-style: italic;
		background: var(--bg-secondary);
		padding: 1rem 1.5rem;
		border-radius: 0 8px 8px 0;
	}

	.markdown-content :global(hr) {
		border: none;
		border-top: 1px solid var(--border-light);
		margin: 2rem 0;
	}

	.markdown-content :global(table) {
		width: 100%;
		border-collapse: collapse;
		margin: 1.5rem 0;
		border: 1px solid var(--border-light);
		border-radius: 8px;
		overflow: hidden;
	}

	.markdown-content :global(th),
	.markdown-content :global(td) {
		padding: 0.75rem 1rem;
		text-align: left;
		border-bottom: 1px solid var(--border-light);
	}

	.markdown-content :global(th) {
		background: var(--bg-secondary);
		font-weight: 600;
		color: var(--text-primary);
	}

	.markdown-content :global(tr:last-child td) {
		border-bottom: none;
	}

	.markdown-content :global(tr:hover) {
		background: var(--bg-hover);
	}

	.markdown-content :global(img) {
		max-width: 100%;
		height: auto;
		border-radius: 8px;
		margin: 1.5rem 0;
		box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
	}

	.markdown-content :global(strong) {
		font-weight: 600;
		color: var(--text-primary);
	}

	.markdown-content :global(em) {
		font-style: italic;
		color: var(--text-secondary);
	}

	/* Mobile optimizations */
	@media (max-width: 768px) {
		.markdown-content :global(h1) {
			font-size: 2rem;
		}

		.markdown-content :global(h2) {
			font-size: 1.5rem;
		}

		.markdown-content :global(h3) {
			font-size: 1.25rem;
		}

		.markdown-content :global(pre) {
			padding: 1rem;
			font-size: 0.8125rem;
		}

		.markdown-content :global(table) {
			font-size: 0.875rem;
		}

		.markdown-content :global(th),
		.markdown-content :global(td) {
			padding: 0.5rem 0.75rem;
		}
	}
</style>